import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import Dialog, { DialogTitle, DialogContent, DialogActions } from "components/common/Dialog";
import CustomButton from "components/common/Button";
import TimeRangeSelector from "components/CheckInCheckOut/TimeRangeSelector";
import "./BreakTimeExceededModal.scss";

function BreakTimeExceededModal({
  open,
  onClose,
  onUpdate,
  breakType,
  breakStartTime = null,
  totalNursingBreakSeconds = 0,
  shiftEndTime = null,
}) {
  const [endTime, setEndTime] = useState(null);
  const [endTimeError, setEndTimeError] = useState("");

  useEffect(() => {
    // Reset end time to null when modal opens
    setEndTime(null);
    setEndTimeError("");
  }, [breakStartTime, breakType, totalNursingBreakSeconds, shiftEndTime]);

  const formatDate = (dateValue) => {
    if (!dateValue) return "";
    const options = { weekday: "long", year: "numeric", month: "short", day: "numeric" };
    return dateValue.toLocaleDateString("en-US", options);
  };

  const calculateDuration = () => {
    if (!breakStartTime || !endTime) return "0 min";

    const diff = Math.floor((endTime - breakStartTime) / 1000); // in seconds
    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);

    if (hours > 0) {
      return `${hours} hr ${minutes} min`;
    }
    return `${minutes} min`;
  };

  const isNextDay = () => {
    if (!breakStartTime || !endTime) return false;
    const startDate = new Date(breakStartTime);
    const endDate = new Date(endTime);

    const startMinutes = startDate.getHours() * 60 + startDate.getMinutes();
    const endMinutes = endDate.getHours() * 60 + endDate.getMinutes();

    if (
      endDate.getDate() !== startDate.getDate() ||
      endDate.getMonth() !== startDate.getMonth() ||
      endDate.getFullYear() !== startDate.getFullYear()
    ) {
      return true;
    }

    return endMinutes < startMinutes;
  };

  const handleEndTimeChange = (newEndTime) => {
    // Validate that end time is within allowed limits
    if (breakStartTime && newEndTime) {
      // Calculate max allowed end time
      let maxEndTime = new Date(breakStartTime);

      if (breakType === "nursing_break" && totalNursingBreakSeconds !== undefined) {
        // Calculate remaining nursing break time (in seconds)
        const oneHourInSeconds = 3600;
        const remainingSeconds = oneHourInSeconds - totalNursingBreakSeconds;

        if (remainingSeconds > 0) {
          maxEndTime.setSeconds(maxEndTime.getSeconds() + remainingSeconds);
        } else {
          maxEndTime = new Date();
        }

        // If shift end time is provided and is earlier than calculated max, use shift end time
        if (shiftEndTime && new Date(shiftEndTime) < maxEndTime) {
          maxEndTime = new Date(shiftEndTime);
        }
      } else if (shiftEndTime) {
        // For regular breaks, max end time is shift end time + 3 hours
        maxEndTime = new Date(shiftEndTime);
        maxEndTime.setHours(maxEndTime.getHours() + 3);
      } else {
        // If no shift end time, default to 1 hour after start time
        maxEndTime.setHours(maxEndTime.getHours() + 1);
      }

      // Create proper date objects for comparison
      const breakStart = new Date(breakStartTime);
      const newEnd = new Date(breakStart);
      newEnd.setHours(newEndTime.getHours(), newEndTime.getMinutes(), 0, 0);

      // If end time appears to be before start time (in minutes), it's likely next day
      const newEndTimeMinutes = newEndTime.getHours() * 60 + newEndTime.getMinutes();
      const breakStartMinutes = breakStart.getHours() * 60 + breakStart.getMinutes();

      if (newEndTimeMinutes < breakStartMinutes) {
        // End time is on the next day
        newEnd.setDate(newEnd.getDate() + 1);
      }

      // Check if end time exceeds max allowed time
      if (newEnd > maxEndTime) {
        setEndTime(newEnd);
        if (breakType === "nursing_break") {
          setEndTimeError("End time cannot exceed total 1 hour nursing break limit or 3 hours after shift end");
        } else {
          setEndTimeError("End time cannot exceed 3 hours after shift end time");
        }
      } else if (newEnd <= breakStart) {
        setEndTime(newEnd);
        setEndTimeError("End time must be after start time");
      } else {
        setEndTime(newEnd);
        setEndTimeError("");
      }
    }
  };

  const handleUpdate = () => {
    if (endTime && !endTimeError) {
      onUpdate(endTime);
      onClose();
    }
  };

  const breakTypeText = breakType === "nursing_break" ? "nursing break" : "break";
  const breakTypeTitle = breakType === "nursing_break" ? "Nursing Break" : "Break";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width="688px"
      title={
        <DialogTitle
          title={
            <div className="break-time-exceeded-modal__title-wrapper">
              <div className="break-time-exceeded-modal__icon-wrapper">
                <WarningAmberOutlinedIcon sx={{ fontSize: 28, color: "#E3382D" }} />
              </div>
              <div className="break-time-exceeded-modal__title-content">
                <h2 className="break-time-exceeded-modal__title heading-h2-semibold">
                  It seems you didn't end your {breakTypeText} on time
                </h2>
                <p className="break-time-exceeded-modal__subtitle para-body-m-regular">
                  Please enter an accurate break time before proceeding with any further actions.
                </p>
              </div>
              <p className="break-time-exceeded-modal__date heading-h4-semibold">
                {formatDate(breakStartTime ? new Date(breakStartTime) : new Date())}
              </p>
            </div>
          }
        />
      }
      content={
        <DialogContent>
          <div className="break-time-exceeded-modal__content">
            <TimeRangeSelector
              heading={`${breakTypeTitle} time`}
              totalDuration={calculateDuration()}
              showHeading
              startTime={breakStartTime ? new Date(breakStartTime) : null}
              endTime={endTime}
              onStartTimeChange={() => {}}
              onEndTimeChange={handleEndTimeChange}
              startLabel="Start time"
              endLabel="End time"
              endTimeError={endTimeError}
              startTimeDisabled
              showArrow
              showDate={false}
              showNextDayLabel={isNextDay()}
              nextDayText="Next day"
            />
          </div>
        </DialogContent>
      }
      actions={
        <DialogActions
          action1={
            <CustomButton
              label="Update Break Time"
              onClick={handleUpdate}
              variant="contained"
              color="error"
              size="large"
              isDisabled={!!endTimeError}
            />
          }
        />
      }
    />
  );
}

BreakTimeExceededModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  breakType: PropTypes.oneOf(["break", "nursing_break"]).isRequired,
  breakStartTime: PropTypes.number,
  totalNursingBreakSeconds: PropTypes.number,
  shiftEndTime: PropTypes.number,
};

export default BreakTimeExceededModal;
